import React, { useEffect, useState } from 'react';
import { api } from '../config/api';
import './EasebuzzPayment.css';

const EasebuzzPayment = ({ 
  amount, 
  currency = 'INR', 
  onSuccess, 
  onError, 
  userDetails,
  bookingDetails,
  bookingId,
  onCreatePendingBooking,
  isCreatingPendingBooking,
  showSummary = true,
  showTerms = true,
  termsAccepted: externalTermsAccepted = null
}) => {
  // Use external termsAccepted if provided, otherwise use internal state
  const [internalTermsAccepted, setInternalTermsAccepted] = useState(false);
  const termsAccepted = externalTermsAccepted !== null ? externalTermsAccepted : internalTermsAccepted;
  const [paymentInitData, setPaymentInitData] = useState(null);
  const [isLoadingPayment, setIsLoadingPayment] = useState(false);
  const [error, setError] = useState(null);

  const [showErrorPopup, setShowErrorPopup] = useState(false);
const [errorMessage, setErrorMessage] = useState('');

const showError = (message) => {
  setErrorMessage(message);
  setShowErrorPopup(true);
};
  
  // Initialize payment when component mounts or when amount/userDetails change
  // Only initialize if Easebuzz is likely configured (don't fail silently on mount)
  useEffect(() => {
    // Only initialize if we have all required data
    // Don't initialize if Easebuzz might not be configured - let user click button first
    // This prevents showing errors immediately on page load
    if (amount > 0 && userDetails && userDetails.email && userDetails.phone) {
      // Initialize silently - don't show errors on mount
      // Wrap in try-catch to ensure no errors bubble up
      (async () => {
        try {
          await initializePayment();
        } catch (err) {
          // Silently handle initialization errors - just log them
          // Do NOT call onError - it triggers alert on page load
          console.warn('Easebuzz initialization failed (may not be configured):', err.message);
        }
      })();
    }
  }, [amount, userDetails?.email, userDetails?.phone]);

  const initializePayment = async () => {
    try {
      setIsLoadingPayment(true);
      setError(null);

      // Call backend to initiate Easebuzz payment
      const response = await api.initiateEasebuzzPayment({
        amount: parseFloat(amount),
        currency: currency,
        customerName: userDetails?.name || 'Customer',
        customerEmail: userDetails?.email,
        customerPhone: userDetails?.phone || userDetails?.mobile || '',
        bookingId: bookingId || 'temp_booking',
        productInfo: `Movie Booking - ${bookingDetails?.movie?.title || 'Ticket'}`,
      });

      if (response.success && response.data) {
        setPaymentInitData(response.data);
      } else {
        throw new Error(response.message || 'Failed to initialize payment');
      }
    } catch (err) {
      // Silently handle initialization errors - don't show alerts on page load
      // Only show error in component UI, don't call onError (which triggers alert)
      console.warn('Easebuzz initialization failed (may not be configured):', err.message);
      setError(err.message || 'Easebuzz payment not available. Please use Razorpay.');
      // Explicitly do NOT call onError here - it triggers alert on page load
      // onError is only for actual payment failures when user clicks button
    } finally {
      setIsLoadingPayment(false);
    }
  };

  const openEasebuzzPayment = async () => {
    if (!termsAccepted) {
      showError('Please agree to the Terms and Conditions to proceed with payment.');
      return;
    }

    // If payment not initialized, try to initialize now
    if (!paymentInitData) {
      try {
        await initializePayment();
        if (!paymentInitData) {
          showError('Payment initialization failed. Please use Razorpay or contact support.');
          return;
        }
      } catch (err) {
        showError('Payment initialization failed. Please use Razorpay or contact support.');
        return;
      }
    }

    // Create pending booking before opening payment gateway (if not already created)
    let finalBookingId = bookingId;
    if (onCreatePendingBooking && !finalBookingId) {
      try {
        finalBookingId = await onCreatePendingBooking();
        if (!finalBookingId) {
          return;
        }
      } catch (error) {
        console.error('Failed to create pending booking:', error);
        return;
      }
    }

    try {
      setIsLoadingPayment(true);

      // Load Easebuzz iFrame script if not already loaded
      if (!window.Easebuzz) {
        const script = document.createElement('script');
        script.src = paymentInitData.iframe_url || 'https://pay.easebuzz.in/pay/';
        script.async = true;
        script.onload = () => {
          if (window.Easebuzz) {
            proceedWithPayment(finalBookingId);
          } else {
            // If Easebuzz script doesn't expose a global, use direct iFrame
            proceedWithDirectIframe(finalBookingId);
          }
        };
        script.onerror = () => {
          // Fallback to direct iFrame if script fails
          proceedWithDirectIframe(finalBookingId);
        };
        document.body.appendChild(script);
      } else {
        proceedWithPayment(finalBookingId);
      }
    } catch (err) {
      console.error('Error opening Easebuzz payment:', err);
      setError(err.message || 'Failed to open payment gateway. Please try again.');
      setIsLoadingPayment(false);
      // Only call onError for actual payment failures (user attempted payment)
      onError && onError(err);
    }
  };

  const proceedWithPayment = (finalBookingId) => {
    // If Easebuzz SDK is available, use it
    if (window.Easebuzz && typeof window.Easebuzz.initiate === 'function') {
      window.Easebuzz.initiate({
        ...paymentInitData,
        onSuccess: (response) => {
          // console.log('Easebuzz Payment Success:', response);
          handlePaymentSuccess(response, finalBookingId);
        },
        onFailure: (error) => {
          console.error('Easebuzz Payment Failed:', error);
          handlePaymentError(error);
        },
      });
    } else {
      // Fallback to direct iFrame
      proceedWithDirectIframe(finalBookingId);
    }
  };

  const proceedWithDirectIframe = (finalBookingId) => {
    // Create a modal with iFrame for Easebuzz payment
    const modal = document.createElement('div');
    modal.id = 'easebuzz-payment-modal';
    modal.className = 'easebuzz-modal-overlay';
    modal.innerHTML = `
      <div class="easebuzz-modal-content">
        <div class="easebuzz-modal-header">
          <h3>Complete Payment</h3>
          <button class="easebuzz-modal-close" id="easebuzz-modal-close-btn">×</button>
        </div>
        <iframe 
          id="easebuzz-payment-iframe"
          src="${paymentInitData.iframe_url}"
          frameborder="0"
          style="width: 100%; height: 600px; border: none;"
          allow="payment"
        ></iframe>
      </div>
    `;
    document.body.appendChild(modal);

    // Close button handler
    const closeBtn = document.getElementById('easebuzz-modal-close-btn');
    if (closeBtn) {
      closeBtn.onclick = () => {
        modal.remove();
        window.removeEventListener('message', handleMessage);
        setIsLoadingPayment(false);
      };
    }

    // Listen for postMessage from Easebuzz iFrame
    const handleMessage = (event) => {
      // Verify origin for security
      if (event.origin !== 'https://pay.easebuzz.in' && event.origin !== 'https://testpay.easebuzz.in') {
        return;
      }

      if (event.data && (event.data.status === 'success' || event.data.status === 'failure')) {
        if (event.data.status === 'success') {
          handlePaymentSuccess(event.data, finalBookingId);
        } else {
          handlePaymentError(event.data);
        }
        // Remove modal and listener
        modal.remove();
        window.removeEventListener('message', handleMessage);
      }
    };

    window.addEventListener('message', handleMessage);

    // Note: Easebuzz will redirect to success/failure URLs after payment
    // The webhook will handle the actual payment confirmation
    // This iframe is for the payment form display
  };

  const handlePaymentSuccess = async (response, finalBookingId) => {
    try {
      setIsLoadingPayment(false);
      
      // Extract transaction ID from response
      // Note: For Easebuzz, the webhook will handle confirmation
      // This callback is for immediate UI feedback if payment completes in iframe
      const transactionId = response.txnid || response.transactionId || response.easebuzzTransactionId || paymentInitData?.txnid;
      
      // Prepare payment data for confirmation
      const paymentData = {
        transactionId: transactionId,
        easebuzzTransactionId: transactionId,
        easebuzz_txnid: transactionId,
        paymentMethod: 'Easebuzz',
        paymentGatewayResponse: response || paymentInitData,
        totalAmount: parseFloat(amount),
      };

      // Call success callback
      // Note: The webhook will actually confirm the booking, but we can show success message
      // Mark as Easebuzz payment
      onSuccess && onSuccess({
        ...paymentData,
        bookingId: finalBookingId,
        paymentMethod: 'Easebuzz',
        // Mark as pending confirmation (webhook will confirm)
        pendingWebhookConfirmation: true,
      });
    } catch (err) {
      console.error('Error processing payment success:', err);
      handlePaymentError(err);
    }
  };

  const handlePaymentError = (error) => {
    setIsLoadingPayment(false);
    setError(error.message || 'Payment failed. Please try again.');
    onError && onError(error);
  };

  return (
    <div className="easebuzz-payment-option">
      {showSummary && (
        <div className="payment-summary">
          <h3>Payment Summary</h3>
          <div className="amount-breakdown">
            <div className="amount-item">
              <span>Movie Tickets</span>
              <span>₹{(bookingDetails?.ticketPrice || 0).toFixed(2)}</span>
            </div>
            <div className="amount-item">
              <span>Snacks</span>
              <span>₹{(bookingDetails?.snacksPrice || 0).toFixed(2)}</span>
            </div>
            <div className="amount-item">
              <span>Taxes & Fees</span>
              <span>₹{(bookingDetails?.taxes || 0).toFixed(2)}</span>
            </div>
            <div className="amount-item total">
              <span>Total Amount</span>
              <span>₹{amount.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      <div className="payment-method-card">
        {error && (
          <div className="payment-error">
            <p>{error}</p>
            <button onClick={initializePayment} className="retry-btn">Retry</button>
          </div>
        )}

        <button 
          className="payment-method-btn easebuzz-btn"
          onClick={openEasebuzzPayment}
          disabled={!termsAccepted || isCreatingPendingBooking || isLoadingPayment || !paymentInitData}
        >
          <div className="payment-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#3395FF"/>
            </svg>
          </div>
          <div className="payment-info">
            <span className="payment-name">Pay with Easebuzz</span>
            <span className="payment-desc">Cards, UPI, Net Banking, Wallets</span>
          </div>
          <div className="payment-arrow">→</div>
        </button>

        {showTerms && (
          <div className="terms-checkbox-container">
            <input
              type="checkbox"
              id="easebuzzTermsAccepted"
              checked={termsAccepted}
              onChange={(e) => {
                if (externalTermsAccepted === null) {
                  setInternalTermsAccepted(e.target.checked);
                }
              }}
              disabled={externalTermsAccepted !== null}
            />
            <label htmlFor="easebuzzTermsAccepted">
              I agree to the <a href="/pages/terms-and-conditions" target="_blank" rel="noopener noreferrer">Terms and Conditions</a>
            </label>
          </div>
        )}

        {(isLoadingPayment || isCreatingPendingBooking) && (
          <div className="payment-loading">
            <div className="spinner"></div>
            <p>{isCreatingPendingBooking ? 'Creating booking...' : 'Initializing payment...'}</p>
          </div>
        )}
      </div>

      {showErrorPopup && (
      <div className="error-popup-overlay" onClick={() => setShowErrorPopup(false)}>
        <div className="error-popup-content" onClick={(e) => e.stopPropagation()}>
          <div className="error-popup-icon">
            <i className="fas fa-exclamation-circle"></i>
          </div>
          <div className="error-popup-message">
            {errorMessage}
          </div>
          <button
            className="error-popup-button"
            onClick={() => setShowErrorPopup(false)}
          >
            OK
          </button>
        </div>
      </div>
    )}
    </div>
  );
};

export default EasebuzzPayment;

